import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DotsThree } from '@phosphor-icons/react';
import { useStaffReauth } from '../../../../../../components/settings/use-staff-reauth';
import { ResetTwoFactorDialog } from '../../../../../../components/settings/reset-two-factor-dialog';
import { SettingCard } from '../../../../../../components/settings/setting-card';
import { Avatar } from '../../../../../../components/ui/avatar';
import { FilterChips } from '../../../../../../components/ui/filter-chips';
import { IconButton } from '../../../../../../components/ui/icon-button';
import { SearchInput } from '../../../../../../components/ui/input';
import { Popover } from '../../../../../../components/ui/popover';
import { Skeleton } from '../../../../../../components/ui/skeleton';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { useToast } from '../../../../../../components/ui/toast';
import { initialsOf } from '../../../../../../lib/initials';
import { formatActivity } from '../../../../../../lib/session-format';
import { staffHealthQuery, type StaffHealthRow } from '../../../../../../lib/workspace-api';
import { ROLE_LABEL } from '../../../../../../lib/staff-nav';
import { NoResults, ErrorState } from '../../../../../../components/ui/state-views';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/staff-security')({
  component: StaffSecurityPage,
});

type Filter = 'all' | 'off' | 'locked';

const isLocked = (s: StaffHealthRow) => Boolean(s.lockedUntil && new Date(s.lockedUntil) > new Date());

function StaffSecurityPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { guard, dialog: reauthDialog } = useStaffReauth();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [resetting, setResetting] = useState<StaffHealthRow>();

  const roster = useQuery({ ...staffHealthQuery, retry: false });
  const all = roster.data ?? [];
  const off = all.filter((s) => !s.twoFactorEnabled);
  const locked = all.filter(isLocked);

  const q = search.trim().toLowerCase();
  const rows = (filter === 'off' ? off : filter === 'locked' ? locked : all).filter(
    (s) => !q || s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q),
  );

  return (
    <>
      <SettingCard
        title="Staff sign-in health"
        subtitle="Reset two-step for someone who lost their phone, or sign them out everywhere."
      >
        <div className="flex flex-wrap items-center gap-(--mbm-space-3) pb-(--mbm-space-4)">
          <SearchInput
            containerClassName="min-w-(--mbm-layout-dialog-width) flex-1 sm:max-w-(--mbm-layout-dialog-width)"
            placeholder="Search staff"
            aria-label="Search staff"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
          <FilterChips<Filter>
            aria-label="Filter staff"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All', count: all.length },
              { value: 'off', label: 'Two-step off', count: off.length },
              { value: 'locked', label: 'Locked', count: locked.length },
            ]}
          />
        </div>

        {roster.isLoading ? (
          <Skeleton className="h-(--mbm-size-list-row-tall) rounded-(--mbm-radius-md)" />
        ) : roster.isError ? (
          <ErrorState title="Couldn’t load your team" onRetry={() => void roster.refetch()} />
        ) : rows.length === 0 ? (
          <NoResults
            query={search}
            description="Try a different name or filter."
            onClear={() => {
              setSearch('');
              setFilter('all');
            }}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-(--mbm-layout-dialog-wide-width) border-collapse text-left">
              <thead>
                <tr className="text-label-small text-text-section uppercase">
                  <th scope="col" className="py-(--mbm-space-3) font-semibold">
                    Person
                  </th>
                  <th scope="col" className="py-(--mbm-space-3) font-semibold">
                    Role
                  </th>
                  <th scope="col" className="py-(--mbm-space-3) font-semibold">
                    Two-step
                  </th>
                  <th scope="col" className="py-(--mbm-space-3) font-semibold">
                    Last sign-in
                  </th>
                  <th scope="col" className="py-(--mbm-space-3) font-semibold">
                    Devices
                  </th>
                  <th scope="col" className="w-(--mbm-size-icon-button)">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-t border-border-line">
                    <th scope="row" className="py-(--mbm-space-3) pr-(--mbm-space-4) text-left font-normal">
                      <span className="flex items-center gap-(--mbm-space-3)">
                        <Avatar initials={initialsOf(s.name, s.email)} size="md" className="bg-brand-primary" />
                        <span className="flex min-w-0 flex-col">
                          <span className="text-label-medium text-text-primary">{s.name}</span>
                          <span className="truncate text-body-xsmall text-text-tertiary">{s.email}</span>
                        </span>
                      </span>
                    </th>
                    <td className="py-(--mbm-space-3) pr-(--mbm-space-4) text-body-compact text-text-secondary">
                      {ROLE_LABEL[s.staffRole]}
                    </td>
                    <td className="py-(--mbm-space-3) pr-(--mbm-space-4)">
                      {isLocked(s) ? (
                        <StatusTag label="LOCKED" tone="red" />
                      ) : (
                        <StatusTag
                          label={s.twoFactorEnabled ? 'ON' : 'OFF'}
                          tone={s.twoFactorEnabled ? 'green' : 'amber'}
                        />
                      )}
                    </td>
                    <td className="py-(--mbm-space-3) pr-(--mbm-space-4) text-body-compact text-text-secondary">
                      {isLocked(s) ? 'Locked' : s.lastSignInAt ? formatActivity(s.lastSignInAt) : 'Never'}
                    </td>
                    <td className="py-(--mbm-space-3) pr-(--mbm-space-4) text-body-compact text-text-secondary">
                      {s.deviceCount}
                    </td>
                    <td className="py-(--mbm-space-3)">
                      <Popover
                        role="menu"
                        className="w-(--mbm-layout-menu-width)"
                        trigger={(props) => (
                          <IconButton
                            icon={DotsThree}
                            variant="ghost"
                            aria-label={`Actions for ${s.name}`}
                            {...props}
                          />
                        )}
                      >
                        {(close) => (
                          <button
                            type="button"
                            role="menuitem"
                            disabled={!s.twoFactorEnabled}
                            title={
                              s.twoFactorEnabled ? undefined : `${s.name.split(/\s+/)[0]} hasn’t turned on two-step`
                            }
                            onClick={() => {
                              close();
                              guard('reset someone’s two-step', () => setResetting(s));
                            }}
                            className="flex w-full items-center px-(--mbm-space-4) py-(--mbm-space-3) text-left text-label-medium text-text-primary hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring) disabled:cursor-not-allowed disabled:text-text-disabled"
                          >
                            Reset two-step…
                          </button>
                        )}
                      </Popover>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingCard>

      {reauthDialog}
      <ResetTwoFactorDialog
        person={resetting}
        onClose={() => setResetting(undefined)}
        onDone={() => {
          toast({
            title: `Two-step reset for ${resetting?.name ?? 'them'}`,
            description: 'It’s saved in the audit log.',
          });
          setResetting(undefined);
          void queryClient.invalidateQueries({ queryKey: staffHealthQuery.queryKey });
        }}
      />
    </>
  );
}
